import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ConnectionStatus from './ConnectionStatus.jsx';
import LanguageSelector from './LanguageSelector.jsx';
import GlobalSearch from './GlobalSearch.jsx';
import CountriesDropdown from './CountriesDropdown.jsx';
import useWebSocket from '../../hooks/useWebSocket.js';
import { useLanguage } from '../../i18n/LanguageContext.jsx';

const REGIONS = [
  { label: '🇮🇳 India',         path: '/india'       },
  { label: '🇺🇸 United States', path: '/us'          },
  { label: '🇷🇺 Russia',        path: '/russia'      },
  { label: '🇨🇳 China',         path: '/china'       },
  { label: '🇳🇴 Norway',        path: '/norway'      },
  { label: '🇩🇪 Germany',       path: '/germany'     },
  { label: '🇦🇿 Azerbaijan',    path: '/azerbaijan'  },
  { label: '🇫🇷 France',        path: '/france'      },
  { label: '🇳🇱 Netherlands',   path: '/netherlands' },
  { label: '🇺🇿 Uzbekistan',    path: '/uzbekistan'  },
  { label: '🇦🇲 Armenia',       path: '/armenia'     },
  { label: '🇪🇸 Spain',         path: '/spain'       },
];

const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Mobile accordion for countries list
const MobileCountries = ({ onNav }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full min-h-[44px] px-1 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300"
      >
        Countries
        <span className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown />
        </span>
      </button>
      {open && (
        <div className="pl-3 pb-2 flex flex-col">
          {REGIONS.map((r) => (
            <Link
              key={r.path}
              to={r.path}
              onClick={onNav}
              className="flex items-center min-h-[44px] px-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              {r.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { status } = useWebSocket();
  const { t } = useLanguage();
  const location = useLocation();
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    const onOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
    };
  }, [mobileOpen]);

  const toggleDark = () => {
    setDark(d => {
      const next = !d;
      document.documentElement.classList.toggle('dark', next);
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800" ref={mobileMenuRef}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0 group min-h-[44px]">
          <img src="/logo-wordmark-transparent.png" alt="Shatranj" className="w-auto block dark:hidden" style={{ height: '6.25rem' }} />
          <img src="/logo-wordmark-dark-transparent.png" alt="Shatranj" className="w-auto hidden dark:block" style={{ height: '6.25rem' }} />
        </Link>

        {/* Right-side controls */}
        <div className="flex items-center gap-1">
          <GlobalSearch />

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
            <Link
              to="/players"
              className="flex items-center min-h-[44px] px-2 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
            >
              Players
            </Link>
            <a
              href="/blogs"
              className="flex items-center min-h-[44px] px-2 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
            >
              Blog
            </a>
          </nav>

          <ConnectionStatus status={status} />

          {/* Dark mode toggle — desktop only */}
          <button
            onClick={toggleDark}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="hidden sm:flex w-11 h-11 rounded-full items-center justify-center text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            {dark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Hamburger — mobile only, 44×44 */}
          <button
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            className="sm:hidden w-11 h-11 flex items-center justify-center rounded-lg text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            {mobileOpen ? <XIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          id="mobile-nav"
          className="sm:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-lg"
        >
          <div className="max-w-6xl mx-auto px-4 py-2 flex flex-col divide-y divide-zinc-100 dark:divide-zinc-800">
            <Link
              to="/players"
              onClick={() => setMobileOpen(false)}
              className="flex items-center min-h-[44px] px-1 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Players
            </Link>
            <a
              href="/blogs"
              className="flex items-center min-h-[44px] px-1 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Blog
            </a>
            <button
              onClick={toggleDark}
              className="flex items-center justify-between w-full min-h-[44px] px-1 text-sm font-medium text-zinc-700 dark:text-zinc-300"
            >
              <span>{dark ? 'Light Mode' : 'Dark Mode'}</span>
              <span className="text-zinc-400 dark:text-zinc-500">{dark ? <SunIcon /> : <MoonIcon />}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
